import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { debounceTime, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { MaterialModule } from 'src/app/material.module';
import { ReportsService } from '../reports.service';
import { LoginService } from 'src/app/login.service';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { LookupService } from 'src/app/services/lookup.service';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { ToastrService } from 'ngx-toastr';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { IPagedPatientReportResult, IPatientReport, IReportRequest } from '../ireports';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { DATE_FORMAT } from 'src/app/common/constant/constants.type';
import { Router } from '@angular/router';
import { ReportColumn, ReportPrintService } from '../../../services/report-print.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-ipd-report',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    ReactiveFormsModule,
    TranslateModule,
    DefaultValuePipe
  ],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './ipd-report.component.html',
  styleUrl: './ipd-report.component.scss'
})
export class IpdReportComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  IpdReportForm: FormGroup;
  doctorControls = new FormControl('');
  doctorslist: CommonDropDown[] = [];
  selectedDoctors: CommonDropDown[] = [];
  isDoctor = false;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  loggedInDoctorId: number | null = null;
  filteredDoctorList$: Observable<CommonDropDown[]>;
  showAddOptionforDoctorList = false;
  minDate: Date;
  maxDate: Date = new Date();

  dataSource = new MatTableDataSource<IPatientReport>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  pagination: PaginationResult;
  ipdreportCount: number = 0;
  ipdreportItems: IPatientReport[] = [];
  displayedColumns = ['ipdDate', 'ipdNo', 'patientName', 'mobile', 'address', 'dischargeDate', 'diagnosis'];
  isLoading: boolean = false;
  isPrintReport: boolean = false;
  patientSearchControl = new FormControl('');

  constructor(
    private _reportService: ReportsService,
    private _loginService: LoginService,
    private hospitalData: HospitalStateService,
    private _lookupService: LookupService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private router: Router,
    private printService: ReportPrintService
  ) { }

  ngOnInit(): void {
    this.isDoctor = this._loginService.isDoctor();

    const now = new Date();
    this.IpdReportForm = this.fb.group({
      doctorIds: [[]],
      startDate: [new Date(now.getFullYear(), now.getMonth(), 1), Validators.required],
      endDate: [new Date(), Validators.required]
    });

    this._lookupService.getDoctors()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.doctorslist = data || [];

        if (this.isDoctor) {
          this.loggedInDoctorId = this._loginService.getUserId();
          this.IpdReportForm.patchValue({
            doctorIds: [this.loggedInDoctorId]
          });

          const doc = this.doctorslist.find(d => d.id === this.loggedInDoctorId);
          if (doc) {
            this.selectedDoctors = [doc];
            this.doctorControls.setValue(doc.name, { emitEvent: false });
          }
        } else {
          this.filteredDoctorList$ = this.doctorControls.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            map((value) => {
              const val = typeof value === 'string' ? value : '';
              const filtered = this._filterDoctorContent(val);
              this.showAddOptionforDoctorList =
                !!val && !filtered.some((x) => x.name.toLowerCase() === val.toLowerCase());
              return filtered;
            })
          );

          // Sync selected doctors with FormGroup
          this.doctorControls.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              const selectedIds = this.selectedDoctors.map(d => d.id);
              this.IpdReportForm.get('doctorIds')?.setValue(selectedIds, { emitEvent: false });
            });
        }
      });

    this._reportService.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
        }
      });

    this._reportService.ipd$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data) {
        this.dataSource.data = data || [];
        this.ipdreportItems = data || []; // Keep reportItems in sync for printing
        this.dataSource.paginator = this.paginator;
        this.ipdreportCount = data?.length || 0;
      }
    });

    this.dataSource.filterPredicate = (data: IPatientReport, filter: string): boolean => {
      const search = filter?.toLowerCase() ?? '';
      const patientName = (data.patientName ?? '').toLowerCase();
      const ipdNo = (data.ipdNo ?? '').toString().toLowerCase();
      return patientName.includes(search) || ipdNo.includes(search);
    };

    this.patientSearchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value: string | null) => {
        const filterValue = (value ?? '').trim().toLowerCase();
        this.dataSource.filter = filterValue;
        if (this.dataSource.paginator) {
          this.dataSource.paginator.firstPage();
        }
      });

    // Auto-search on load
    this.OnSearch();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  private _filterDoctorContent(value: string): CommonDropDown[] {
    const filterValue = value.toLowerCase();
    return this.doctorslist.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDoctorToggle(doc: CommonDropDown): void {
    const index = this.selectedDoctors.findIndex(d => d.id === doc.id);

    if (index > -1) {
      this.selectedDoctors.splice(index, 1);
    } else {
      this.selectedDoctors.push(doc);
    }

    this.IpdReportForm.get('doctorIds')?.setValue(this.selectedDoctors.map(d => d.id));

    this.doctorControls.setValue(
      this.selectedDoctors.map(d => d.name).join(', '),
      { emitEvent: false }
    );
  }

  isDoctorSelected(id: number): boolean {
    return this.selectedDoctors.some(d => d.id === id);
  }

  OnSearch(): void {
    if (this.IpdReportForm.invalid) {
      return;
    }

    const formValue = this.IpdReportForm.getRawValue();

    if (formValue.startDate && formValue.endDate) {
      const startDate = new Date(formValue.startDate);
      const endDate = new Date(formValue.endDate);

      if (endDate < startDate) {
        this.toastr.error(this.translate.instant('DATE_COMPARE_START_END'));
        return;
      }
    }

    const requestData: IReportRequest = {
      ...formValue,
      startDate: formValue.startDate && this.datePipe.transform(formValue.startDate, DATE_FORMAT),
      endDate: formValue.endDate && this.datePipe.transform(formValue.endDate, DATE_FORMAT),
      pageNumber: 0,
      pageSize: 0
    };

    this.isLoading = true;
    this._reportService.getPatientIpdReport(requestData).subscribe({
      next: (response: BaseRespone<IPagedPatientReportResult>) => {
        this.isLoading = false;
        this.isPrintReport = true;
        if (!response.isSuccess) {
          this.toastr.error(response.message || 'Error fetching report');
        }
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error('Error fetching report');
      }
    });
  }


  clearFilters() {
    this.isPrintReport = false;
    const now = new Date();

    if (!this.isDoctor) {
      this.selectedDoctors = [];
      this.doctorControls.setValue('', { emitEvent: false });
      this.IpdReportForm.patchValue({
        doctorIds: []
      });
    }

    this.patientSearchControl.setValue('');
    this.dataSource.filter = '';

    this.IpdReportForm.patchValue({
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date()
    });
    this.OnSearch();
  }

  viewIPD(row: IPatientReport) {
    if (row.patientId && row.ipdId) {
      this.router.navigate(['/ipd/admit', row.ipdId, 'patient', row.patientId]);
    }
  }

  async printReport() {
    const { startDate, endDate, doctorIds } = this.IpdReportForm.getRawValue();
    const columns: ReportColumn[] = [
      {
        header: 'IPD Date', field: 'ipdDate', flex: '2',
        formatter: (val) => this.datePipe.transform(val, 'dd-MM-yyyy') ?? ''
      },
      { header: 'IPD No', field: 'ipdNo', flex: '2' },
      { header: 'Patient Name', field: 'patientName', flex: '3' },
      { header: 'Mobile', field: 'mobile', flex: '2' },
      { header: 'Address', field: 'address', flex: '2' },
      {
        header: 'DIS. Date', field: 'dischargeDate', flex: '2',
        formatter: (val) => this.datePipe.transform(val, 'dd-MM-yyyy') ?? ''
      },
      { header: 'Diagnosis', field: 'diagnosis', flex: '3' },
    ];

    const doctorArray = Array.isArray(doctorIds)
      ? doctorIds
      : (doctorIds ? [doctorIds] : []);

    if (doctorArray.length === 0) {
      columns.push({
        header: 'Doctor',
        field: 'doctorName',
        flex: '2'
      });
    }

    await this.printService.printReport({
      title: this.translate.instant('LABEL.PATIENT_REPORT_IPD'),
      items: this.ipdreportItems,
      columns: columns,
      startDate: startDate,
      endDate: endDate,
      doctorNames: this.selectedDoctors.map(d => d.name).join(', ')
    });
  }
}
